import { Box, Stack } from "@mui/material";
import { darken } from "@mui/material/styles";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { onboardingTokens } from "../theme";
import { t } from "../../../locales/t";

/**
 * Whether one demographic answer is shown on the member's profile.
 *
 * The pill shape is shared with `Pill`, but not its colours: a selected `Pill`
 * is a solid teal fill because it is an answer, while this stays a tint with
 * teal text because it describes a state rather than being one of a set.
 *
 * Labelled by its current state, not by the action — "Hidden from profile"
 * next to `aria-pressed` reads one way, whereas a button labelled "Hide" is
 * ambiguous about whether it describes now or next.
 *
 * @param {object} props
 * @param {boolean} props.visible
 * @param {(next: boolean) => void} props.onChange
 * @param {string} props.describedBy  id of the question's legend, so the button
 *   announces which answer it governs — there are four identical ones.
 */
/**
 * `disabled` exists because visibility must follow the answer (ECH-379). A
 * dimension with nothing selected has nothing to show, and letting it be
 * switched on records a member as displaying an answer they never gave.
 */
const ProfileVisibilityToggle = ({
  visible,
  disabled,
  onChange,
  describedBy,
}) => {
  const Icon = visible ? VisibilityOutlinedIcon : VisibilityOffOutlinedIcon;

  return (
    <Stack
      component="button"
      type="button"
      direction="row"
      alignItems="center"
      gap={0.75}
      onClick={() => onChange(!visible)}
      disabled={disabled}
      aria-pressed={visible}
      aria-describedby={describedBy}
      sx={(theme) => ({
        flexShrink: 0,
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        fontSize: "0.75rem",
        fontWeight: 600,
        fontFamily: onboardingTokens.fontBody,
        color: visible
          ? onboardingTokens.teal
          : onboardingTokens.mutedForeground,
        backgroundColor: visible
          ? onboardingTokens.lightTeal
          : onboardingTokens.muted,
        border: `1px solid ${visible ? "rgba(1, 190, 204, 0.2)" : "transparent"}`,
        transition: theme.transitions.create(
          ["background-color", "border-color", "color"],
          { duration: theme.transitions.duration.shortest },
        ),
        "@media (hover: hover)": {
          "&:hover": {
            backgroundColor: darken(
              visible ? onboardingTokens.lightTeal : onboardingTokens.muted,
              0.04,
            ),
          },
        },
        "&:focus-visible": {
          outline: `2px solid ${onboardingTokens.teal}`,
          outlineOffset: 2,
        },
      })}
    >
      <Icon aria-hidden="true" sx={{ fontSize: 14 }} />
      <Box component="span">
        {t(
          visible
            ? "onboarding.demographics.visibleOnProfile"
            : "onboarding.demographics.hiddenFromProfile",
        )}
      </Box>
    </Stack>
  );
};

export default ProfileVisibilityToggle;
